import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";
import { getInitials, resolveImageUrlCandidates } from "../../utils/googleDriveImage";

function TestimonialCard({ testimonial, compact = false }) {
  const [candidateIndex, setCandidateIndex] = useState(0);
  const imageCandidates = useMemo(
    () => resolveImageUrlCandidates(testimonial.studentPhoto),
    [testimonial.studentPhoto]
  );
  const imageUrl = imageCandidates[candidateIndex] || "";
  const initials = getInitials(testimonial.name);

  useEffect(() => {
    setCandidateIndex(0);
  }, [testimonial.studentPhoto]);

  const handleImageError = () => {
    setCandidateIndex((current) => {
      if (current + 1 < imageCandidates.length) {
        return current + 1;
      }
      return imageCandidates.length;
    });
  };

  const shouldShowImage = Boolean(imageUrl) && candidateIndex < imageCandidates.length;

  return (
    <motion.article
      whileHover={{ y: -6, scale: 1.01 }}
      transition={{ duration: 0.28, ease: "easeOut" }}
      className={`relative card-elevated rounded-2xl p-6 lg:p-8 ${
        compact ? "" : "h-full"
      }`}
    >
      <div className="absolute top-0 left-6 right-6 h-[3px] bg-gradient-to-r from-royal-blue/10 via-royal-blue/60 to-royal-blue/10 rounded-b" />
      
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="relative h-12 w-12 overflow-hidden rounded-full border-2 border-white shadow-soft bg-sky-glow flex-shrink-0">
            {shouldShowImage ? (
              <img
                src={imageUrl}
                alt={`${testimonial.name}`}
                className="h-full w-full object-cover"
                loading="lazy"
                onError={handleImageError}
              />
            ) : (
              <span className="inline-flex h-full w-full items-center justify-center text-sm font-bold text-midnight-navy">
                {initials}
              </span>
            )}
          </div>

          <div>
            <p className="text-sm font-bold text-midnight-navy">{testimonial.name}</p>
            <p className="mt-0.5 text-[11px] font-bold uppercase tracking-[0.12em] text-royal-blue">
              {testimonial.country} · {testimonial.course}
            </p>
          </div>
        </div>

        <Quote className="h-6 w-6 text-royal-blue/20" aria-hidden="true" />
      </div>

      <p className={`mt-6 leading-relaxed text-slate-text/80 ${compact ? "text-sm" : "text-base"}`}>
        "{testimonial.testimonial}"
      </p>

      <div className="mt-6 flex items-center gap-1 text-amber-400">
        <Star className="h-4 w-4 fill-current" aria-hidden="true" />
        <Star className="h-4 w-4 fill-current" aria-hidden="true" />
        <Star className="h-4 w-4 fill-current" aria-hidden="true" />
        <Star className="h-4 w-4 fill-current" aria-hidden="true" />
        <Star className="h-4 w-4 fill-current" aria-hidden="true" />
      </div>
    </motion.article>
  );
}

export default TestimonialCard;
